import { useEffect, useState } from "react";
import {
  Button as AntButton,
  Drawer,
  Input as AntInput,
  Select as AntSelect,
  Space,
  Tabs,
} from "antd";
import { get, isNil } from "lodash";
import { useTranslation } from "react-i18next";
import { BiTrash } from "react-icons/bi";
import { MdOutlineDashboard, MdOutlineLink } from "react-icons/md";
import Button from "shared/components/Button";
import Input from "shared/components/Input";
import Select from "shared/components/Select";
import Switch from "shared/components/Switch";
import { useLazyQuery, useMutation } from "shared/hooks/useApi";
import { NAME_SPACES } from "shared/locales/constants";
import graphql from "utils/api/graphql";
import { NODE_TYPES, PROCESS_TAGS } from "utils/constants";
import { isPublishBlocked } from "../../ScreenStudios/Detail/BindingReview";
import BindingReport from "./BindingReport";
import ConnectorListField from "./ConnectorListField";
import DataBindingListField from "./DataBindingListField";
import MirrorStatusListField from "./MirrorStatusListField";
import NodeConfigPanel, { FormDefinitionBuilder } from "./NodeConfigPanel";
import { NODE_TYPE_VALUES } from "./node-types";
import ScreenWorkshop from "./ScreenWorkshop";
import ScreenThumbnail from "./ScreenThumbnail";

const StatusPanel = ({
  node,
  nodes = [],
  persisted,
  processId,
  variables = [],
  onChange,
  onDelete,
  onClose,
  reload,
}) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.ADMIN.PROCESS);
  const FORM = t("FORM", { returnObjects: true });
  const STATUSES = FORM.STATUSES;
  const CONNECTORS = (FORM.STATUSES && FORM.STATUSES.CONNECTORS) || {};
  const DATA = (FORM.STATUSES && FORM.STATUSES.DATA) || {};
  const TABS = (FORM.STATUSES && FORM.STATUSES.TABS) || {};
  const NODE_TYPE_LABELS = FORM.NODE_TYPES || {};
  const CONFIG = FORM.CONFIG || {};
  const TAGS = t("PROCESS_TAGS", { returnObjects: true });

  const data = node ? node.data : {};
  const statusId = node ? node.id : null;
  const nodeType = data.nodeType || NODE_TYPES.STATUS;
  const isStatusType = nodeType === NODE_TYPES.STATUS;
  const hasNodeConfig = !isStatusType && nodeType !== NODE_TYPES.FORM_STEP;
  const nodeTypeOptions = NODE_TYPE_VALUES.map((value) => ({
    value,
    label: NODE_TYPE_LABELS[value] || value,
  }));
  const hasPreview = !isNil(data.screenHtml) && data.screenHtml !== "";
  const tagOptions = Object.entries(PROCESS_TAGS).map(([key, value]) => ({
    value,
    label: TAGS[key] || key,
  }));

  const [projectId, setProjectId] = useState("");
  const [screens, setScreens] = useState(null);
  const [workshopOpen, setWorkshopOpen] = useState(false);

  // This panel is mounted once and re-fed as the selection moves, so
  // anything it holds outlives the node it was opened on. An operator who
  // leaves the workshop open and picks another node would otherwise find it
  // still open, now pointing at a different screen.
  useEffect(() => {
    setProjectId("");
    setScreens(null);
    setWorkshopOpen(false);
  }, [statusId]);

  const [loadScreens, { loading: loadingScreens }] = useLazyQuery(
    graphql.queries.UI_PROVIDER_SCREENS,
    { onCompleted: (result) => setScreens(result?.uiProviderScreens || []) },
  );

  const [importScreens, { loading: importing }] = useMutation(
    graphql.mutations.IMPORT_UI_PROVIDER_SCREENS,
    { onCompleted: () => reload && reload() },
  );

  const [detectSlots, { loading: detecting }] = useMutation(
    graphql.mutations.DETECT_SLOTS,
    { onCompleted: () => reload && reload() },
  );

  const [publishVersion, { loading: publishing }] = useMutation(
    graphql.mutations.PUBLISH_SCREEN_VERSION,
    { onCompleted: () => reload && reload() },
  );

  const handleLoadScreens = () => {
    if (!projectId.trim()) return;
    loadScreens({
      variables: { provider: { project: { id: projectId.trim() } } },
    });
  };

  const handleBindScreen = (externalId) => {
    if (!externalId || !statusId) return;
    const screen = (screens || []).find((s) => s.externalId === externalId);
    importScreens({
      variables: {
        provider: { project: { id: projectId.trim() } },
        mappings: [
          {
            externalId,
            status: { id: statusId },
            htmlContent: screen && screen.htmlContent,
            name: screen && screen.name,
          },
        ],
      },
    });
  };

  const handleDetectSlots = () =>
    detectSlots({ variables: { screenVersion: { id: data.screenVersionId } } });

  const handlePublish = () =>
    publishVersion({ variables: { where: { id: data.screenVersionId } } });

  const screenOptions = (screens || []).map((screen) => ({
    value: screen.externalId,
    label: `${(screen.name || "").split("/").pop() || screen.externalId} - ${screen.externalId}`,
    previewUrl: screen.previewUrl,
  }));

  const binding = !isNil(data.screenVersionId);
  const published = data.screenVersionStatus === "published";
  const publishBlocked = isPublishBlocked(data.screenReport);
  const mutating = importing || detecting || publishing;

  const screenFrom = get(data, "config.screenFrom") || null;
  const sharedScreenOptions = nodes
    .filter((item) => item.id !== statusId && !isNil(item.data.screenVersionId))
    .map((item) => ({
      value: item.id,
      label: item.data.name || item.id,
    }));

  const handleScreenFromChange = (value) => {
    const config = { ...(data.config || {}) };
    if (value) config.screenFrom = value;
    else delete config.screenFrom;
    onChange("config", config);
  };

  const handleOpenSubscriptionOnEnterChange = (checked) => {
    const config = { ...(data.config || {}) };
    if (checked) config.openSubscriptionOnEnter = true;
    else delete config.openSubscriptionOnEnter;
    onChange("config", config);
  };

  const generalTab = (
    <Space direction="vertical" size={16} className="canvas-editor__full-width">
      <Input
        label={STATUSES.NAME}
        placeholder={STATUSES.NAME}
        value={data.name || ""}
        onChange={(e) => onChange("name", e.target.value)}
      />
      <Select
        showSearch
        label={STATUSES.NODE_TYPE || "Type"}
        placeholder={STATUSES.NODE_TYPE || "Type"}
        options={nodeTypeOptions}
        value={nodeType}
        onChange={(value) => onChange("nodeType", value)}
      />
      <Select
        showSearch
        label={STATUSES.TAG}
        placeholder={STATUSES.TAG}
        options={tagOptions}
        value={data.tag || undefined}
        onChange={(value) => onChange("tag", value)}
      />
      <Input
        label={STATUSES.PROGRESS}
        placeholder={STATUSES.PROGRESS}
        value={data.progress || ""}
        onChange={(e) => onChange("progress", e.target.value)}
      />
      <Input
        label={STATUSES.COLOR || "Couleur"}
        placeholder={STATUSES.COLOR || "Couleur"}
        value={data.color || ""}
        onChange={(e) => onChange("color", e.target.value)}
      />
      <div>
        <div className="canvas-editor__section-title">
          {STATUSES.MOVE_TO_NEXT}
        </div>
        <Switch
          checked={!!data.moveToNext}
          onChange={(checked) => onChange("moveToNext", checked)}
        />
      </div>
      <div>
        <div className="canvas-editor__section-title">
          {STATUSES.OPEN_SUBSCRIPTION_ON_ENTER}
        </div>
        <Switch
          checked={!!get(data, "config.openSubscriptionOnEnter")}
          onChange={handleOpenSubscriptionOnEnterChange}
        />
        <div className="canvas-editor__meta">
          {STATUSES.OPEN_SUBSCRIPTION_ON_ENTER_HINT}
        </div>
      </div>
      <div>
        <div className="canvas-editor__section-title">
          {STATUSES.MIRROR_TITLE}
        </div>
        <MirrorStatusListField
          actions={data.actions || []}
          processId={processId}
          variables={variables}
          conditionLabels={FORM.POSSIBLE_STATUSES?.CONDITIONS || {}}
          labels={STATUSES}
          onChange={(next) => onChange("actions", next)}
        />
      </div>
    </Space>
  );

  const formTab = (
    <Space direction="vertical" size={16} className="canvas-editor__full-width">
      <FormDefinitionBuilder
        formDefinition={data.formDefinition}
        variables={variables}
        labels={CONFIG}
        onChange={(next) => onChange("formDefinition", next)}
      />
      {hasNodeConfig ? (
        <div>
          <div className="canvas-editor__section-title">{CONFIG.TITLE}</div>
          <NodeConfigPanel
            nodeType={nodeType}
            config={data.config || {}}
            labels={CONFIG}
            onConfigChange={(next) => onChange("config", next)}
          />
        </div>
      ) : null}
    </Space>
  );

  const connectorsTab = (
    <Space direction="vertical" size={16} className="canvas-editor__full-width">
      <div>
        <div className="canvas-editor__section-title">{CONNECTORS.ENTRY}</div>
        <ConnectorListField
          value={data.entryConnectors || []}
          onChange={(next) => onChange("entryConnectors", next)}
          labels={CONNECTORS}
        />
      </div>
      <div>
        <div className="canvas-editor__section-title">{CONNECTORS.OUTPUT}</div>
        <ConnectorListField
          value={data.outputConnectors || []}
          onChange={(next) => onChange("outputConnectors", next)}
          labels={CONNECTORS}
        />
      </div>
    </Space>
  );

  const dataTab = (
    <DataBindingListField
      value={data.dataBindings || []}
      onChange={(next) => onChange("dataBindings", next)}
      labels={DATA}
    />
  );

  const screenTab = (
    <Space direction="vertical" size={16} className="canvas-editor__full-width">
      <div>
        <div className="canvas-editor__section-title">{STATUSES.TEMPLATE}</div>
        {hasPreview ? (
          <ScreenThumbnail
            html={data.screenHtml}
            styleContent={data.screenStyleContent}
            size="md"
            title={data.name}
          />
        ) : (
          <div className="canvas-editor__meta">
            {data.screenCount
              ? `${data.screenCount} screen(s), none published`
              : "No screen"}
          </div>
        )}
        {!isNil(data.screenReport) ? (
          <div className="canvas-editor__subsection">
            <div className="canvas-editor__section-title">
              {STATUSES.BINDING_REPORT}
            </div>
            <BindingReport report={data.screenReport} />
          </div>
        ) : null}
        <div className="canvas-editor__subsection">
          {persisted ? (
            <AntButton
              icon={<MdOutlineDashboard />}
              onClick={() => setWorkshopOpen(true)}
            >
              {STATUSES.WORKSHOP}
            </AntButton>
          ) : (
            <div className="canvas-editor__meta">
              {STATUSES.SAVE_BEFORE_EDIT || STATUSES.SAVE_BEFORE_BIND}
            </div>
          )}
          {/*
            Keyed by the node so that changing node builds a new workshop
            rather than re-feeding the old one. It holds fifteen pieces of
            state — the pasted markup among them — and closing resets four.
            Without this key, opening the workshop on one node, switching to
            the code view, closing, then opening another node left the panes
            holding the first node's markup: saving wrote it into the second
            screen, silently.
          */}
          <ScreenWorkshop
            key={statusId}
            processId={processId}
            open={workshopOpen}
            onClose={() => setWorkshopOpen(false)}
            title={data.name}
            screenVersionId={data.screenVersionId}
            statusId={statusId}
            screenId={data.screenId}
            versionNumber={data.screenVersionNumber}
            htmlContent={data.screenHtml || ""}
            styleContent={data.screenStyleContent || ""}
            slots={data.screenSlots}
            manifestId={data.screenManifestId}
            states={data.screenStates}
            variables={variables}
            conditionLabels={FORM.POSSIBLE_STATUSES?.CONDITIONS || {}}
            published={published}
            publishBlocked={publishBlocked}
            labels={{ ...STATUSES, ...(STATUSES.STATES || {}) }}
            reload={reload}
          />
        </div>
      </div>
      <div>
        <div className="canvas-editor__section-title">
          {STATUSES.SHARED_SCREEN}
        </div>
        <AntSelect
          allowClear
          showSearch
          virtual={false}
          className="canvas-editor__full-width"
          placeholder={STATUSES.SHARED_SCREEN_PLACEHOLDER}
          optionFilterProp="label"
          options={sharedScreenOptions}
          value={screenFrom || undefined}
          onChange={handleScreenFromChange}
        />
        <div className="canvas-editor__meta">
          {binding && screenFrom
            ? STATUSES.SHARED_SCREEN_IGNORED
            : STATUSES.SHARED_SCREEN_HINT}
        </div>
      </div>
      <div>
        <div className="canvas-editor__section-title">
          {STATUSES.BIND_SCREEN}
        </div>
        {persisted ? (
          <Space
            direction="vertical"
            size={8}
            className="canvas-editor__full-width"
          >
            <div className="canvas-editor__row">
              <AntInput
                placeholder={
                  STATUSES.PROJECT_PLACEHOLDER || "Lien ou ID du projet Stitch"
                }
                value={projectId}
                onChange={(e) => setProjectId(e.target.value)}
                prefix={<MdOutlineLink />}
              />
              <AntButton
                onClick={handleLoadScreens}
                loading={loadingScreens}
                disabled={!projectId.trim()}
              >
                {STATUSES.LOAD_SCREENS || "Charger les écrans"}
              </AntButton>
            </div>
            {!isNil(screens) ? (
              <AntSelect
                showSearch
                virtual={false}
                listHeight={320}
                className="canvas-editor__full-width"
                placeholder={STATUSES.SELECT_SCREEN}
                loading={importing}
                disabled={mutating}
                optionFilterProp="label"
                options={screenOptions}
                onChange={handleBindScreen}
              />
            ) : null}
            <div className="canvas-editor__row">
              <AntButton
                onClick={handleDetectSlots}
                loading={detecting}
                disabled={!binding || mutating}
              >
                {STATUSES.DETECT_SLOTS || "Détecter les slots"}
              </AntButton>
              <AntButton
                type="primary"
                onClick={handlePublish}
                loading={publishing}
                disabled={!binding || published || publishBlocked || mutating}
              >
                {STATUSES.PUBLISH || "Publier"}
              </AntButton>
            </div>
          </Space>
        ) : (
          <div className="canvas-editor__meta">{STATUSES.SAVE_BEFORE_BIND}</div>
        )}
      </div>
    </Space>
  );

  const tabItems = [
    { key: "general", label: TABS.GENERAL || "Général", children: generalTab },
    { key: "form", label: TABS.FORM || "Formulaire", children: formTab },
    {
      key: "connectors",
      label: TABS.CONNECTORS || "Connecteurs",
      children: connectorsTab,
    },
    { key: "data", label: TABS.DATA || "Données", children: dataTab },
    ...(isStatusType
      ? [{ key: "screen", label: TABS.SCREEN || "Écran", children: screenTab }]
      : []),
  ];

  return (
    <Drawer
      open={!isNil(node)}
      onClose={onClose}
      width="60%"
      title={data.name || STATUSES.NAME}
      footer={
        <Button type="danger--outlined" onClick={onDelete}>
          <BiTrash size={18} /> {FORM.DELETE_STATUS || "Supprimer le statut"}
        </Button>
      }
    >
      <Tabs items={tabItems} />
    </Drawer>
  );
};

export default StatusPanel;
